import * as React from 'react';
import { cn } from '../utils/cn';

// ─── Radio Context ──────────────────────────────────────────────────────

interface RadioContextValue {
  name?: string;
  value?: string | number;
  onChange?: (value: string | number) => void;
  size?: 'large' | 'default' | 'small';
  buttonStyle?: 'outline' | 'solid';
  disabled?: boolean;
}

const RadioContext = React.createContext<RadioContextValue>({});

// ─── Radio.Group ────────────────────────────────────────────────────────

export interface RadioGroupProps {
  /** Controlled selected value */
  value?: string | number;
  /** Default selected value */
  defaultValue?: string | number;
  /** Change handler — receives event with target.value (antd compat) */
  onChange?: (e: { target: { value: string | number } }) => void;
  /** Size for all children */
  size?: 'large' | 'default' | 'small';
  /** Button style: outline or solid */
  buttonStyle?: 'outline' | 'solid';
  /** Disable all children */
  disabled?: boolean;
  /** Additional class names */
  className?: string;
  /** Inline style */
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

export function RadioGroup({
  value: controlledValue,
  defaultValue,
  onChange,
  size = 'default',
  buttonStyle = 'outline',
  disabled,
  className,
  style,
  children,
}: RadioGroupProps) {
  const [internalValue, setInternalValue] = React.useState(defaultValue);
  const value = controlledValue !== undefined ? controlledValue : internalValue;

  const handleChange = (val: string | number) => {
    setInternalValue(val);
    if (onChange) {
      onChange({ target: { value: val } });
    }
  };

  return (
    <RadioContext.Provider value={{ value, onChange: handleChange, size, buttonStyle, disabled }}>
      <div className={cn('flex flex-wrap gap-0', className)} style={style} role="radiogroup">
        {children}
      </div>
    </RadioContext.Provider>
  );
}

// ─── Radio ──────────────────────────────────────────────────────────────

export interface RadioProps {
  /** Radio value */
  value: string | number;
  /** Disable this option */
  disabled?: boolean;
  /** Additional class names */
  className?: string;
  /** Inline style */
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

function RadioBase({ value, disabled: localDisabled, className, style, children }: RadioProps) {
  const ctx = React.useContext(RadioContext);
  const isChecked = ctx.value === value;
  const isDisabled = localDisabled || ctx.disabled;

  return (
    <label
      className={cn(
        'inline-flex items-center gap-2 cursor-pointer select-none',
        isDisabled && 'opacity-40 cursor-not-allowed',
        className
      )}
      style={style}
    >
      <div className="relative flex items-center justify-center">
        <input
          type="radio"
          checked={isChecked}
          onChange={() => ctx.onChange?.(value)}
          disabled={isDisabled}
          className={cn(
            'peer h-5 w-5 shrink-0 appearance-none rounded-full',
            'border-2 border-input bg-white',
            'checked:border-primary',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'disabled:cursor-not-allowed',
            'transition-colors duration-fast ease-smooth'
          )}
        />
        {/* Inner dot */}
        <div className="pointer-events-none absolute h-2.5 w-2.5 rounded-full bg-primary scale-0 peer-checked:scale-100 transition-transform duration-fast ease-smooth" />
      </div>
      {children && (
        <span className="text-sm font-body text-foreground">{children}</span>
      )}
    </label>
  );
}

// ─── Radio.Button ───────────────────────────────────────────────────────

const buttonSizeStyles: Record<string, string> = {
  large: 'h-12 px-5 text-sm',
  default: 'h-10 px-4 text-xs',
  small: 'h-8 px-3 text-xs',
};

export interface RadioButtonProps {
  /** Radio value */
  value: string | number;
  /** Disable this option */
  disabled?: boolean;
  /** Default checked (antd compat, usually handled by group) */
  defaultChecked?: boolean;
  /** Controlled checked state (antd compat) */
  checked?: boolean;
  /** Additional class names */
  className?: string;
  /** Inline style */
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

export function RadioButton({
  value,
  disabled: localDisabled,
  className,
  style,
  children,
}: RadioButtonProps) {
  const ctx = React.useContext(RadioContext);
  const isChecked = ctx.value === value;
  const isDisabled = localDisabled || ctx.disabled;
  const isSolid = ctx.buttonStyle === 'solid';

  return (
    <button
      type="button"
      role="radio"
      aria-checked={isChecked}
      disabled={isDisabled}
      onClick={() => ctx.onChange?.(value)}
      className={cn(
        'inline-flex items-center justify-center border font-body font-medium',
        'normal-case tracking-normal',
        'transition-all duration-fast ease-smooth',
        '-ml-px first:ml-0 first:rounded-l-sm last:rounded-r-sm',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:z-10',
        'disabled:pointer-events-none disabled:opacity-40',
        buttonSizeStyles[ctx.size || 'default'],
        isChecked
          ? isSolid
            ? 'bg-primary text-primary-foreground border-primary z-10'
            : 'bg-primary/5 text-primary border-primary z-10'
          : 'bg-white text-foreground border-border hover:bg-surface-raised',
        className
      )}
      style={style}
    >
      {children}
    </button>
  );
}

// Compound component for antd compat: Radio.Group, Radio.Button
export const Radio = Object.assign(RadioBase, {
  Group: RadioGroup,
  Button: RadioButton,
});
